/**
 * Desktop cache and outbox, sitting in front of `api()`.
 *
 * Reads: a successful GET is mirrored; a network failure is served from that
 * mirror. Writes to an existing document: applied locally first, then sent;
 * if the hop fails they wait in the outbox with the version the server last
 * acknowledged. A 409 never overwrites the local copy — that is the whole
 * point of keeping a library on disk.
 *
 * The website does not import this. `api()` calls in only when `isDesktopApp`.
 */

import { APP_EVENTS } from "@/lib/brand";
import { appFetch } from "@/lib/runtime";
import { getAccessToken, reportOffline, reportSuccess } from "@/lib/session";
import { isNetworkError } from "@/lib/network";
import {
  applyLocalDocumentWrite,
  filterCachedDocuments,
  localSearchNeedle,
  parseApiPath,
  textMatches,
} from "@/lib/desktop-sync-core";
import {
  conflictOutboxCount,
  deleteEntity,
  deleteOutbox,
  enqueueOutbox,
  getEntity,
  getResponse,
  listEntities,
  listOutbox,
  markOutboxConflict,
  pendingOutboxCount,
  putEntity,
  putResponse,
} from "@/lib/desktop-store";
import type { Document, Folder, Note, Quote } from "@/types";
import type { SearchResults } from "@/components/search/SearchPalette";

export interface ServerCall {
  path: string;
  init?: RequestInit;
}

type CachedKind = "document" | "folder" | "note" | "quote" | "sermon";

function kindForPath(pathname: string): CachedKind | null {
  if (pathname.startsWith("/api/documents")) return "document";
  if (pathname.startsWith("/api/folders")) return "folder";
  if (pathname.startsWith("/api/notes")) return "note";
  if (pathname.startsWith("/api/quotes")) return "quote";
  if (pathname.startsWith("/api/sermons")) return "sermon";
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

async function rememberPayload(pathname: string, payload: unknown) {
  const kind = kindForPath(pathname);
  if (Array.isArray(payload)) {
    if (kind === "document") {
      for (const item of payload) {
        const row = asRecord(item);
        const nested = asRecord(row?.document);
        const entity = (nested ?? row) as { id?: number } | null;
        if (entity && typeof entity.id === "number") {
          await putEntity("document", entity.id, nested ?? item);
        }
        if (kind === "document" && row && typeof row.document_id === "number" && nested) {
          await putEntity("sermon", row.document_id, item);
        }
      }
    } else if (kind) {
      for (const item of payload) {
        const row = asRecord(item);
        if (row && typeof row.id === "number") await putEntity(kind, row.id, item);
        if (kind === "sermon" && row && typeof row.document_id === "number") {
          await putEntity("sermon", row.document_id, item);
          const nested = asRecord(row.document);
          if (nested && typeof nested.id === "number") {
            await putEntity("document", nested.id, nested);
          }
        }
      }
    }
    return;
  }

  const row = asRecord(payload);
  // Note routes answer `{ note: {...} }`; unwrap so the row itself is cached.
  if (kind === "note" && row) {
    const nested = asRecord(row.note);
    if (nested && typeof nested.id === "number") {
      await putEntity("note", nested.id, nested);
      return;
    }
  }
  if (kind === "document" && row && typeof row.id === "number") {
    await putEntity("document", row.id, payload);
  }
  if (kind === "folder" && row && typeof row.id === "number") {
    await putEntity("folder", row.id, payload);
  }
  if (kind === "note" && row && typeof row.id === "number") {
    await putEntity("note", row.id, payload);
  }
  if (kind === "quote" && row && typeof row.id === "number") {
    await putEntity("quote", row.id, payload);
  }
  if (kind === "sermon" && row && typeof row.document_id === "number") {
    await putEntity("sermon", row.document_id, payload);
  }
}

async function cacheGet(requestPath: string, payload: unknown) {
  await putResponse(requestPath, payload);
  const { path } = parseApiPath(requestPath);
  await rememberPayload(path, payload);
}

const DOCUMENT_ITEM = /^\/api\/documents\/(\d+)$/;
const DOCUMENT_COLLECTION = /^\/api\/documents\/?$/;
const NOTES_COLLECTION = /^\/api\/notes\/?$/;
const QUOTES_COLLECTION = /^\/api\/quotes\/?$/;

async function serveCachedGet(requestPath: string): Promise<unknown | null> {
  const exact = await getResponse<unknown>(requestPath);
  if (exact !== null) return exact;

  const { path, search } = parseApiPath(requestPath);

  const documentMatch = DOCUMENT_ITEM.exec(path);
  if (documentMatch) {
    return getEntity("document", Number(documentMatch[1]));
  }

  if (DOCUMENT_COLLECTION.test(path)) {
    const [documents, folders] = await Promise.all([
      listEntities<Document>("document"),
      listEntities<Folder>("folder"),
    ]);
    return filterCachedDocuments(documents, search, folders);
  }

  if (path === "/api/folders") return listEntities("folder");
  if (path === "/api/quotes") return listEntities("quote");
  if (path === "/api/notes" || path === "/api/notes/library") return listEntities("note");

  if (path === "/api/search") {
    return localSearch(new URLSearchParams(search.startsWith("?") ? search.slice(1) : search));
  }

  return null;
}

async function localSearch(params: URLSearchParams): Promise<SearchResults> {
  const needle = localSearchNeedle(params.get("q") || params.get("tag") || "");
  const [documents, notes, quotes] = await Promise.all([
    listEntities<Document>("document"),
    listEntities<Note>("note"),
    listEntities<Quote>("quote"),
  ]);

  const tag = (params.get("tag") || "").replace(/^#/, "").toLowerCase();
  const documentHits = documents.filter((document) => {
    if (document.is_archived) return false;
    if (tag) return textMatches(document.content, `#${tag}`) || textMatches(document.title, tag);
    return textMatches(document.title, needle) || textMatches(document.content, needle);
  });

  return {
    documents: documentHits.map((document) => ({
      id: document.id,
      title: document.title,
      content: document.content,
      updated_at: document.updated_at,
    })),
    notes: notes.filter((note) => textMatches(note.content, needle)),
    quotes: quotes.filter(
      (quote) => textMatches(quote.content, needle) || textMatches(quote.author, needle),
    ),
    scripture: [],
    tags: [],
  };
}

function jsonBody(init?: RequestInit): Record<string, unknown> | null {
  if (typeof init?.body !== "string") return null;
  try {
    const parsed = JSON.parse(init.body);
    return asRecord(parsed);
  } catch {
    return null;
  }
}

async function applyOptimisticDocumentPut(pathname: string, init?: RequestInit): Promise<Document | null> {
  const match = DOCUMENT_ITEM.exec(pathname);
  if (!match) return null;
  const id = Number(match[1]);
  const current = await getEntity<Document>("document", id);
  const updates = jsonBody(init);
  if (!current || !updates) return current;
  const next = applyLocalDocumentWrite(current, updates);
  await putEntity("document", id, next);
  return next;
}

let draining = false;

/**
 * Replay queued document writes, in order, with the version they were based on.
 *
 * Stops a single document when the server reports a conflict so a newer sermon
 * cannot be overwritten by a laptop that went to sleep on an older one. Other
 * documents keep draining.
 */
export async function drainOutbox(send: (call: ServerCall) => Promise<Response>) {
  if (draining) return;
  draining = true;
  try {
    const rows = await listOutbox("pending");
    for (const row of rows) {
      let response: Response;
      try {
        response = await send({
          path: row.endpoint,
          init: {
            method: row.method,
            headers: { "Content-Type": "application/json" },
            body: row.body ?? undefined,
          },
        });
      } catch (error) {
        if (isNetworkError(error)) {
          reportOffline();
          return;
        }
        throw error;
      }

      if (response.status === 409) {
        await markOutboxConflict(row.id);
        window.dispatchEvent(
          new CustomEvent(APP_EVENTS.syncConflict, {
            detail: { documentId: row.document_id },
          }),
        );
        continue;
      }

      if (!response.ok) return;

      const payload = await response.json().catch(() => null);
      const { path } = parseApiPath(row.endpoint);
      if (payload) await rememberPayload(path, payload);
      // A creation queued offline was showing under a temporary id; the server
      // row above replaces it, so the placeholder retires here.
      if (row.local_id != null) {
        const kind = kindForPath(path);
        if (kind) await deleteEntity(kind, row.local_id).catch(() => undefined);
      }
      await deleteOutbox(row.id);
      if (payload && asRecord(payload)?.id) {
        window.dispatchEvent(
          new CustomEvent(APP_EVENTS.documentSynced, { detail: { document: payload } }),
        );
      }
    }
    reportSuccess();
  } finally {
    draining = false;
  }
}

/** Drain with the standard authenticated transport — for menu items, the sync
 * indicator, and anything else that wants "sync now" without rebuilding it. */
export function drainOutboxNow(): Promise<void> {
  return drainOutbox(async ({ path, init }) => {
    const token = await getAccessToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return appFetch(path, { ...init, headers });
  });
}

export interface SyncSnapshot {
  pending: number;
  conflicts: number;
}

export async function getSyncSnapshot(): Promise<SyncSnapshot> {
  const [pending, conflicts] = await Promise.all([
    pendingOutboxCount(),
    conflictOutboxCount(),
  ]);
  return { pending, conflicts };
}

export async function desktopGet<T>(
  requestPath: string,
  send: () => Promise<T>,
): Promise<T> {
  try {
    const payload = await send();
    await cacheGet(requestPath, payload).catch(() => undefined);
    reportSuccess();
    void drainOutbox(async ({ path, init }) => {
      const token = await getAccessToken();
      const headers = new Headers(init?.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return appFetch(path, { ...init, headers });
    });
    return payload;
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    reportOffline();
    const cached = await serveCachedGet(requestPath);
    if (cached !== null) return cached as T;
    throw error;
  }
}

/** Distinct enough that two offline captures in the same millisecond differ,
 * and negative so it can never collide with a server id. */
function temporaryId(): number {
  return -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
}

/**
 * File a note or quote locally when the hop is down.
 *
 * Capture is the weekday loop — a thought on a train must not depend on the
 * train's Wi-Fi. The entity appears in lists immediately under a temporary
 * negative id; the queued POST replays later and the drain swaps in the
 * server's row.
 */
async function captureOffline<T>(
  path: string,
  requestPath: string,
  method: string,
  body: Record<string, unknown>,
): Promise<T | null> {
  const now = new Date().toISOString();

  if (NOTES_COLLECTION.test(path)) {
    const note: Note = {
      id: temporaryId(),
      content: String(body.content ?? ""),
      user_id: "",
      parent_type: (body.parent_type as Note["parent_type"]) ?? "standalone",
      parent_id: typeof body.parent_id === "number" ? body.parent_id : null,
      created_at: now,
      updated_at: now,
    };
    await putEntity("note", note.id, note);
    await enqueueOutbox({ method, endpoint: requestPath, body, localId: note.id });
    return { note } as T;
  }

  if (QUOTES_COLLECTION.test(path)) {
    const quote: Quote = {
      id: temporaryId(),
      content: String(body.content ?? ""),
      author: String(body.author ?? ""),
      source: typeof body.source === "string" ? body.source : null,
      created_at: now,
      updated_at: now,
    };
    await putEntity("quote", quote.id, quote);
    await enqueueOutbox({ method, endpoint: requestPath, body, localId: quote.id });
    return quote as T;
  }

  return null;
}

export async function desktopMutate<T>(
  requestPath: string,
  method: string,
  send: () => Promise<T>,
  init?: RequestInit,
): Promise<T> {
  const { path } = parseApiPath(requestPath);
  const documentPut = method === "PUT" && DOCUMENT_ITEM.test(path);
  const body = jsonBody(init);
  const documentId = documentPut ? Number(path.split("/").pop()) : null;

  if (documentPut) {
    await applyOptimisticDocumentPut(path, init).catch(() => undefined);
  }

  try {
    const payload = await send();
    await rememberPayload(path, payload).catch(() => undefined);
    reportSuccess();
    return payload;
  } catch (error) {
    if (!isNetworkError(error)) throw error;

    if (documentPut) {
      reportOffline();
      await enqueueOutbox({
        method,
        endpoint: requestPath,
        body,
        documentId,
        expectedVersion:
          typeof body?.expected_version === "number" ? body.expected_version : null,
      });
      const local = await getEntity<Document>("document", documentId ?? -1);
      if (local) return local as T;
    }

    if (method === "POST" && body) {
      const captured = await captureOffline<T>(path, requestPath, method, body).catch(
        () => null,
      );
      if (captured !== null) {
        reportOffline();
        return captured;
      }
    }

    throw error;
  }
}
